import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { colors, getRiskLevel, riskColors } from "@/theme/eventsTokens.ts";
import { countryName } from "@/utils/countryName.ts";
import { ipActivityIpLink } from "@/utils/ipActivityLink.ts";
import { relativeTime } from "@/utils/relativeTime.ts";
import type { ExposureEntry, ExposureSource } from "@/types";

/** The expanded body of a targeted-account row: its hostile sources and the full hash. */
export default function AccountDetails({
  entry,
  windowDays,
}: {
  readonly entry: ExposureEntry;
  readonly windowDays: number;
}) {
  return (
    <Box sx={{ pl: 4.5, pb: 2 }}>
      <SourcesTable sources={entry.sources} windowDays={windowDays} />
      <HashPanel hash={entry.username_hash} />
    </Box>
  );
}

function SourcesTable({ sources, windowDays }: { readonly sources: ExposureSource[]; readonly windowDays: number }) {
  return (
    <Table size="small" sx={{ mb: 2, "& td, & th": { borderColor: colors.borderLight, px: 1 } }}>
      <TableHead>
        <TableRow>
          <TableCell>Source</TableCell>
          <TableCell>Country</TableCell>
          <TableCell>Network</TableCell>
          <TableCell>Risk</TableCell>
          <TableCell align="right">Attempts</TableCell>
          <TableCell>Last seen</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sources.map((s) => (
          <SourceRow key={s.ip_address} source={s} windowDays={windowDays} />
        ))}
      </TableBody>
    </Table>
  );
}

function SourceRow({ source, windowDays }: { readonly source: ExposureSource; readonly windowDays: number }) {
  return (
    <TableRow>
      <TableCell>
        <MuiLink
          component={RouterLink}
          to={ipActivityIpLink(windowDays, source.ip_address)}
          sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 500 }}
        >
          {source.ip_address}
        </MuiLink>
      </TableCell>
      <TableCell>{source.country_code ? countryName(source.country_code) : "—"}</TableCell>
      <TableCell sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {source.asn ? `${source.asn} · ${source.as_name ?? ""}` : "—"}
      </TableCell>
      <TableCell>
        <RiskCell score={source.fraud_score} />
      </TableCell>
      <TableCell align="right">{source.failures.toLocaleString()}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{relativeTime(source.last_seen) ?? "—"}</TableCell>
    </TableRow>
  );
}

// A source with no scored snapshot row yet has no score; the row still lists.
function RiskCell({ score }: { readonly score?: number | null }) {
  if (score == null) return <>—</>;
  return (
    <Chip
      label={score}
      size="small"
      sx={{
        backgroundColor: riskColors[getRiskLevel(score)].main,
        color: colors.primaryForeground,
        fontWeight: 700,
        fontSize: "0.75rem",
        minWidth: 40,
        height: 22,
      }}
    />
  );
}

// The full hash the customer's collector sent. It is SHA-256/512 of the
// lowercased username (ip-shield API contract), unsalted, so the customer can
// reproduce it from their own user list and match it against their logs —
// Insight never learns the username.
function HashPanel({ hash }: { readonly hash: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard?.writeText(hash);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable (insecure context / denied): the hash is still selectable.
    }
  }
  return (
    <Box>
      <Typography
        variant="overline"
        sx={{ display: "block", color: colors.textMuted, fontWeight: 600, letterSpacing: "0.08em", mb: 0.5 }}
      >
        Full hash
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Box
          component="code"
          sx={{
            flex: 1,
            minWidth: 0,
            fontFamily: "monospace",
            fontSize: 12,
            wordBreak: "break-all",
            bgcolor: colors.backgroundMuted,
            borderRadius: 1,
            px: 1,
            py: 0.5,
          }}
        >
          {hash}
        </Box>
        <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={copy} aria-label="Copy hash">
          {copied ? "Copied" : "Copy"}
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        This is the hash your collector sent: SHA-256 (or SHA-512) of the lowercased username. Hash
        your own user list the same way to see which account this is — the username itself never
        reaches Insight.
      </Typography>
    </Box>
  );
}
